import { ref, nextTick } from 'vue'
import { getAboutList } from '@/apis/informationDetail.js'
import { createQuotesPollingManager } from '../utils/polling'
import { limitQuotes } from '../logic'
import { colorObj, colorObj_black } from '../utils/constants'
import { toFixed } from '@/utils'
import NP from 'number-precision'
import { formatPublishTime } from '../utils/time'
import { getReadingNums } from '@/utils'

export function useRelatedData() {
    const quotes = ref([])
    const newsList = ref([])
    const quotesLoading = ref(false)
    const newsLen = ref(5)
    const favoriteLocks = new Map()
    let _quotesPolling = null

    const disposeQuotes = (data, theme, colorType) => {
        if (!Array.isArray(data)) return []
        return data.map(item => {
            const colorarr = theme === 'white' ? colorObj : colorObj_black
            if (item.chgPct > 0) {
                item.styleObject = { color: colorarr[colorType].rise }
            } else if (item.chgPct < 0) {
                item.styleObject = { color: colorarr[colorType].fall }
            } else {
                item.styleObject = { color: colorarr[colorType].zero }
            }
            item.favorStatus = !!item.favorStatus
            item.chgPctText = !item.chgPct || item.chgPct == 0 ? '0.00%' : toFixed(NP.divide(item.chgPct, 100), 2) + '%'
            item.nightChgText =
                !item.nightQuote?.chgPct || item.nightQuote?.chgPct == 0 ? '0.00%' : toFixed(NP.divide(item.nightQuote.chgPct, 100), 2) + '%'
            const power = item.mkt == 'us' ? 3 : item.power
            item.priceText = !item.price || item.price == 0 ? '0.00' : toFixed(item.price / Math.pow(10, item.power), power)
            item.nightPriceText =
                !item.nightQuote?.price || item.nightQuote?.price == 0 ? '0.00' : toFixed(item.nightQuote.price / Math.pow(10, item.power), power)
            if (item.tradeStatus === 3) item.priceText = '--'
            item.code = item.mkt + item.rawSymbol
            return item
        })
    }

    const updateQuoteFavorites = async (currentQuotes, jsBridge) => {
        if (!jsBridge || !Array.isArray(currentQuotes)) return
        try {
            // 仅将 Promise.all 改为串行调用，避免 WebView 多路并发触发 InvalidAccessError；单次失败吞掉不抛出，其余逻辑与原来一致
            const res = []
            for (let i = 0; i < currentQuotes.length; i++) {
                const code = currentQuotes[i]?.code
                if (code == null || code === '') {
                    res[i] = null
                    continue
                }
                try {
                    res[i] = await jsBridge.checkFavstock(code)
                } catch (e) {
                    res[i] = null
                }
            }
            res.forEach((favor, idx) => {
                if (quotes.value[idx]) {
                    const code = quotes.value[idx].code
                    const lockUntil = favoriteLocks.get(code) || 0
                    const now = Date.now()
                    if (now < lockUntil) return
                    // Direct mutation is reactive for array elements in Vue 3/2.7+ composition API
                    quotes.value[idx].favorStatus = !!favor?.isFavStock
                }
            })
        } catch (e) {
            // Ignore error
        }
    }

    const lockFavorite = (code, ms = 1000) => {
        favoriteLocks.set(code, Date.now() + ms)
        setTimeout(() => {
            if (favoriteLocks.get(code) && Date.now() >= favoriteLocks.get(code)) {
                favoriteLocks.delete(code)
            }
        }, ms + 50)
    }

    const getList = async ({ stocks, id, theme, colorType, jsBridge }) => {
        const params = {
            code: stocks,
            num: newsLen.value,
            id: id,
        }
        try {
            const { data } = await getAboutList(params)
            const { result, error } = data
            if (!error) {
                newsList.value = (result.list || []).map(item => {
                    item.quotes = []
                    item.publishText = formatPublishTime(item.publish)
                    item.readNum = getReadingNums(item.extra.readNum)
                    return item
                })

                const relatedQuotes = limitQuotes(result.quotes)
                quotes.value = relatedQuotes.length > 0 ? disposeQuotes(relatedQuotes, theme, colorType) : []
                updateQuoteFavorites(quotes.value, jsBridge)
            }
        } catch (e) {
            console.error(e)
        }
    }

    const initPolling = ({ id, stocks, theme, colorType, jsBridge, onTick }) => {
        _quotesPolling = createQuotesPollingManager({
            intervalMs: 5000,
            fetcher: async (pollingStocks) => {
                const { data } = await getAboutList({
                    code: pollingStocks,
                    num: newsLen.value,
                    id: id,
                })
                const { result, error } = data || {}
                if (!error && result) {
                    const relatedQuotes = limitQuotes(result.quotes)
                    return relatedQuotes.length > 0 ? disposeQuotes(relatedQuotes, theme, colorType) : []
                }
                return []
            },
            onData: (nextQuotes) => {
                const oldKey = (quotes.value || []).map(q => `${q.code}:${q.priceText}:${q.chgPctText}`).join('|')
                const newKey = (nextQuotes || []).map(q => `${q.code}:${q.priceText}:${q.chgPctText}`).join('|')
                if (oldKey !== newKey) {
                    quotes.value = nextQuotes
                    updateQuoteFavorites(quotes.value, jsBridge)
                }
            },
            onStatus: ({ loading }) => {
                quotesLoading.value = !!loading
            },
            onTick: () => {
                if (onTick) onTick()
            }
        })

        // Visibility handling
        document.addEventListener('visibilitychange', _quotesPolling.onVisibilityChange, { passive: true })

        // Start polling
        _quotesPolling.start(stocks, false)
    }

    const stopPolling = () => {
        if (_quotesPolling) {
            document.removeEventListener('visibilitychange', _quotesPolling.onVisibilityChange)
            _quotesPolling.stop()
        }
    }

    return {
        quotes,
        newsList,
        quotesLoading,
        newsLen,
        getList,
        initPolling,
        stopPolling,
        disposeQuotes,
        updateQuoteFavorites,
        lockFavorite
    }
}
